import type { ScanCaptureDetails } from "../types/scanWorkflow";

export const FULL_FIDELITY_SCAN_TARGET_MS = 3000 as const;
export const FULL_FIDELITY_PERFORMANCE_CONTRACT = "full-fidelity-shutter-step2-performance-v1" as const;

export type FullFidelityPerformanceVerdict = "pass" | "open" | "not_eligible";

export interface FullFidelityPerformanceEvidence {
  schema_version: 1;
  contract_version: typeof FULL_FIDELITY_PERFORMANCE_CONTRACT;
  target_ms: typeof FULL_FIDELITY_SCAN_TARGET_MS;
  full_fidelity_required: true;
  quality_preservation_required: true;
  recognition_features_disabled_for_performance: false;
  duration_ms: number | null;
  eligible: boolean;
  verdict: FullFidelityPerformanceVerdict;
  reason_codes: string[];
  required_stages: Record<string, "done" | "missing" | "error" | "skipped" | "other">;
  detection_accepted: boolean;
  rectification_completed: boolean;
  ocr_completed: boolean;
}

const REQUIRED_STAGE_IDS = [
  "capture",
  "mapping",
  "crop",
  "quality",
  "detection",
  "geometry",
  "rectification",
  "ocr",
] as const;

function stageState(details: ScanCaptureDetails, stageId: string): "done" | "missing" | "error" | "skipped" | "other" {
  const step = details.processingSteps.find((item) => item.id === stageId);
  if (!step) return "missing";
  if (step.status === "done") return "done";
  if (step.status === "error") return "error";
  if (step.status === "skipped") return "skipped";
  return "other";
}

export function fullFidelityPerformanceEvidence(details: ScanCaptureDetails | null): FullFidelityPerformanceEvidence {
  const durationMs = details?.recognitionTiming?.shutterToStep2DataReadyMs ?? null;
  const requiredStages = Object.fromEntries(
    REQUIRED_STAGE_IDS.map((stageId) => [stageId, details ? stageState(details, stageId) : "missing"]),
  ) as FullFidelityPerformanceEvidence["required_stages"];
  const detectionAccepted = Boolean(details?.detection?.found && details?.detection?.finalGeometryAccepted);
  const rectificationCompleted = Boolean(details?.rectification?.used && !details?.rectification?.error);
  const ocrCompleted = Boolean(details?.ocr?.status === "done" && !details?.ocr?.error);
  const reasonCodes: string[] = [];

  if (durationMs === null) reasonCodes.push("DURATION_MISSING");
  if (!detectionAccepted) reasonCodes.push("DETECTION_NOT_ACCEPTED");
  if (!rectificationCompleted) reasonCodes.push("RECTIFICATION_NOT_COMPLETED");
  if (!ocrCompleted) reasonCodes.push("OCR_NOT_COMPLETED");
  for (const [stageId, status] of Object.entries(requiredStages)) {
    if (status !== "done") reasonCodes.push(`STAGE_${stageId.toUpperCase().replaceAll("-", "_")}_${status.toUpperCase()}`);
  }

  const uniqueReasons = [...new Set(reasonCodes)];
  const eligible = uniqueReasons.length === 0;
  const verdict: FullFidelityPerformanceVerdict = !eligible
    ? "not_eligible"
    : (durationMs ?? Number.POSITIVE_INFINITY) <= FULL_FIDELITY_SCAN_TARGET_MS
      ? "pass"
      : "open";

  return {
    schema_version: 1,
    contract_version: FULL_FIDELITY_PERFORMANCE_CONTRACT,
    target_ms: FULL_FIDELITY_SCAN_TARGET_MS,
    full_fidelity_required: true,
    quality_preservation_required: true,
    recognition_features_disabled_for_performance: false,
    duration_ms: durationMs,
    eligible,
    verdict,
    reason_codes: uniqueReasons,
    required_stages: requiredStages,
    detection_accepted: detectionAccepted,
    rectification_completed: rectificationCompleted,
    ocr_completed: ocrCompleted,
  };
}
